/**
 * opSnapshotStager — the ONE staging/commit state machine for the hydra
 * `oddspapi` channel (HYDRA_API.md §"OddsPapi odds", contract rules 2/2b–2f,
 * 9; workspace:odds-papi-parallel-feed DNL-002/004/005).
 *
 * Pure and transport-agnostic: BOTH the SharedWorker path and the direct-WS
 * fallback drive an instance of this module with raw wire frames — there is
 * deliberately no divergent inline staging logic anywhere else. Two
 * instances exist at runtime:
 *
 *   - hydraWorker.ts holds one against the UPSTREAM socket (hydra's
 *     per-connection generation namespace). Its committed cache is what
 *     greet() replays to late-attaching tabs — re-framed as a synthesized
 *     atomic burst in the worker's OWN generation namespace, because raw
 *     upstream generations reset per upstream connection and would violate
 *     per-connection monotonicity at a tab that outlives a reconnect.
 *   - useHydra holds one per firehose client against whatever the transport
 *     delivers (raw hydra frames in direct mode; worker-synthesized bursts +
 *     verbatim-relayed live frames in shared mode). Identical semantics in
 *     both modes — the tab-side code has zero mode branching.
 *
 * Contract semantics implemented here:
 *   rule 2   — snapshots stage and commit atomically on a complete,
 *              count-matching End; truncated bursts discard whole; prior
 *              state stays; bounded-backoff re-request.
 *   rule 2b  — causal ordering is `rev`, never arrival order; staging keeps
 *              the max-rev value per key; post-commit stale stragglers drop.
 *   rule 2d  — per-game revs are retained across tombstones (session,
 *              within the committed epoch): a same-epoch commit MERGES the
 *              burst's revs over the retained map (max-wins) rather than
 *              wiping it — a wholesale replace would forget a tombstoned
 *              game's rev and re-admit its stale stragglers, violating 2d.
 *   rule 2e  — split committed/staging epoch authority; live frames route
 *              by epoch; an epoch-CHANGING commit rebases authority
 *              wholesale (epoch + rev map) so a hydra restart re-establishes
 *              itself; unknown-epoch frames reject + schedule re-snapshot.
 *   rule 2f  — stale-generation fence: the ACTIVE generation is the latest
 *              Begin; older generations' frames/Ends are ignored + counted
 *              and never touch active staging; a generation newer than any
 *              seen Begin is a protocol anomaly (ignored + counted).
 *   rule 9   — a game absent from a committed snapshot is dropped
 *              everywhere (the commit callback hands the COMPLETE set).
 *
 * The stager holds the committed value state itself (it needs it for the
 * rev guards anyway); consumers mirror it via the callbacks.
 */

import type { HydraOpOddsMsg, HydraOpSnapshotBeginMsg, HydraOpSnapshotEndMsg } from '../clients/HydraClient';
import { promoteToGameKeyV2 } from '../services/analyticsGameKey';

export interface OpStagerCallbacks {
  /** A snapshot burst committed: `frames` is the COMPLETE new committed
   *  value set (tombstoned/absent games already excluded — rule 9).
   *  Consumers atomically replace their entire OP state with it. */
  onCommit: (frames: HydraOpOddsMsg[]) => void;
  /** A live (non-burst) frame passed the epoch + rev guards against
   *  committed state and carries markets — upsert this game. */
  onLiveUpsert: (frame: HydraOpOddsMsg) => void;
  /** A live tombstone (`markets: []`) passed the guards — delete this game
   *  (keyed by canonicalGameKeyV2). */
  onLiveDelete: (gameKeyV2: string, frame: HydraOpOddsMsg) => void;
  /** Ask the transport for a fresh `oddspapi` snapshot. Fired through the
   *  bounded backoff below — never in a tight loop. */
  onRequestSnapshot: () => void;
  /** Counted diagnostics (stale straggler, stale generation, mixed epoch,
   *  …). Counters are also kept internally — see `diagnostics`. */
  onDiagnostic?: (kind: OpStagerDiagnostic) => void;
}

export type OpStagerDiagnostic =
  | 'staleStraggler'          // live frame, rev ≤ committed rev (rule 2b)
  | 'staleStagingFrame'       // burst frame, rev ≤ staged rev for the key
  | 'unknownEpoch'            // live frame matching neither authority (2e)
  | 'staleGeneration'         // older-generation frame/End/Begin (rule 2f)
  | 'generationAnomaly'       // generation newer than any seen Begin (2f)
  | 'mixedEpochBurst'         // burst frame/End epoch ≠ Begin epoch (2e)
  | 'countMismatch'           // End gameCount ≠ frames received (rule 2)
  | 'orphanFrame'             // generation-tagged frame with no open burst
  | 'truncatedBurst';         // Begin/disconnect discarded an open burst

interface Staging {
  generation: number;
  epoch: string;
  /** Max-rev frame per gameKeyV2 (rule 2b) — tombstones stage too. */
  frames: Map<string, HydraOpOddsMsg>;
  revs: Map<string, number>;
  /** Generation-tagged frames RECEIVED this burst (arrival count, not
   *  unique keys) — compared against End.gameCount (rule 2). */
  received: number;
}

/** Bounded backoff for snapshot re-requests (rule 2): 1s → 30s cap. */
const REREQUEST_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000];

/** A burst that hasn't Ended within this window was pacing-aborted server
 *  side (a burst that can't complete aborts WITHOUT an End — rule 2c) or
 *  lost in transit: discard staging whole + re-request. Well above hydra's
 *  drain pacing; the DNL-005 fence keeps the old burst inert if it does
 *  straggle in after the timeout fired. */
export const OP_BURST_TIMEOUT_MS = 30_000;

type ScheduleFn = (fn: () => void, ms: number) => unknown;
type CancelFn = (handle: unknown) => void;

export class OpSnapshotStager {
  private readonly cb: OpStagerCallbacks;
  private readonly scheduleFn: ScheduleFn;
  private readonly cancelFn: CancelFn;

  private committedEpoch: string | null = null;
  /** Committed value state; tombstoned games absent. */
  private committed = new Map<string, HydraOpOddsMsg>();
  /** Per-game rev authority — RETAINED across tombstones (rule 2d), so it
   *  can hold keys the value map does not. */
  private committedRevs = new Map<string, number>();
  private staging: Staging | null = null;
  /** Highest Begin generation seen on the CURRENT connection (rule 2f
   *  namespace) — null before the first Begin / after a reconnect reset. */
  private lastBeginGeneration: number | null = null;
  private everCommitted = false;

  private rerequestTimer: unknown = null;
  private rerequestAttempts = 0;
  private burstTimeoutTimer: unknown = null;

  readonly diagnostics: Record<OpStagerDiagnostic, number> = {
    staleStraggler: 0,
    staleStagingFrame: 0,
    unknownEpoch: 0,
    staleGeneration: 0,
    generationAnomaly: 0,
    mixedEpochBurst: 0,
    countMismatch: 0,
    orphanFrame: 0,
    truncatedBurst: 0,
  };

  constructor(
    callbacks: OpStagerCallbacks,
    opts: { scheduleFn?: ScheduleFn; cancelFn?: CancelFn } = {},
  ) {
    this.cb = callbacks;
    this.scheduleFn = opts.scheduleFn ?? ((fn, ms) => setTimeout(fn, ms));
    this.cancelFn = opts.cancelFn ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  }

  /** True once any snapshot has committed — gates worker greet replay so a
   *  cold worker doesn't wipe a tab's state with an empty synthetic burst. */
  get hasCommitted(): boolean { return this.everCommitted; }

  /** The committed value set (insertion-ordered) — worker greet replay. */
  getCommittedFrames(): HydraOpOddsMsg[] {
    return [...this.committed.values()];
  }

  getCommittedEpoch(): string | null { return this.committedEpoch; }

  private diag(kind: OpStagerDiagnostic): void {
    this.diagnostics[kind]++;
    this.cb.onDiagnostic?.(kind);
  }

  private keyOf(msg: HydraOpOddsMsg): string {
    // Bucket by the doubleheader-safe key (rule 3); the promote fallback
    // covers a (contractually absent) missing v2 field defensively.
    return msg.op.canonicalGameKeyV2 || promoteToGameKeyV2(msg.op.canonicalGameKey);
  }

  /** Transport (re)connect boundary: generations are per-connection, so the
   *  namespace resets; an open burst can never complete (its End died with
   *  the connection) — discard it; committed authority SURVIVES (the
   *  reconnect's snapshot re-request rebases it properly via commit). */
  notifyDisconnect(): void {
    if (this.staging) {
      this.staging = null;
      this.diag('truncatedBurst');
    }
    this.lastBeginGeneration = null;
    this.cancelRerequest();
    this.cancelBurstTimeout();
    // Reconnect re-snapshots at onopen, so no re-request is scheduled here.
  }

  begin(msg: HydraOpSnapshotBeginMsg): void {
    if (this.lastBeginGeneration != null && msg.generation <= this.lastBeginGeneration) {
      // A delayed Begin from a superseded burst — rule 2f's fence applies
      // to Begins too: accepting it would regress the active generation.
      this.diag('staleGeneration');
      return;
    }
    if (this.staging) {
      // New Begin supersedes an open burst: discard staging whole, keep
      // prior committed state (rule 2) — the NEW burst is now active.
      this.staging = null;
      this.diag('truncatedBurst');
    }
    this.lastBeginGeneration = msg.generation;
    this.staging = {
      generation: msg.generation,
      epoch: msg.sourceEpoch,
      frames: new Map(),
      revs: new Map(),
      received: 0,
    };
    // Truncation watchdog: a pacing-aborted burst never sends its End
    // (rule 2c) — without this, staging would sit open forever on a quiet
    // channel. The timeout discards + re-requests; a stale continuation of
    // the timed-out burst is inert under the generation fence... except its
    // generation is still the latest Begin, so bump the namespace floor to
    // make the dead burst's stragglers reject as stale (DNL-005 shape).
    this.cancelBurstTimeout();
    this.burstTimeoutTimer = this.scheduleFn(() => {
      this.burstTimeoutTimer = null;
      if (this.staging?.generation === msg.generation) {
        this.staging = null;
        this.diag('truncatedBurst');
        this.scheduleRerequest();
      }
    }, OP_BURST_TIMEOUT_MS);
  }

  frame(msg: HydraOpOddsMsg): void {
    if (msg.generation != null) {
      this.burstFrame(msg);
    } else {
      this.liveFrame(msg);
    }
  }

  private burstFrame(msg: HydraOpOddsMsg): void {
    const gen = msg.generation!;
    if (this.staging == null) {
      if (this.lastBeginGeneration != null && gen <= this.lastBeginGeneration) {
        // Tail of a discarded/committed burst — inert (rule 2f).
        this.diag('staleGeneration');
      } else {
        // Burst frame with no Begin ever seen (mid-burst attach) or from a
        // generation newer than any Begin — protocol anomaly (rule 2f).
        this.diag(this.lastBeginGeneration == null ? 'orphanFrame' : 'generationAnomaly');
      }
      return;
    }
    if (gen < this.staging.generation) {
      this.diag('staleGeneration');
      return;
    }
    if (gen > this.staging.generation) {
      this.diag('generationAnomaly');
      return;
    }
    if (msg.sourceEpoch !== this.staging.epoch) {
      // Mixed-epoch burst is invalid (rule 2e): discard staging whole,
      // keep prior committed state, re-request.
      this.staging = null;
      this.diag('mixedEpochBurst');
      this.scheduleRerequest();
      return;
    }
    // Arrival count for the End integrity check — counted whether or not
    // the rev guard stages the frame (the server counts frames SENT).
    this.staging.received++;
    this.stageUnderRevGuard(msg);
  }

  /** Stage into the open burst under the rev guard (rule 2b: staging keeps
   *  the max-rev value per key — an older snapshot frame never overwrites a
   *  newer live value that landed in staging, and vice versa). */
  private stageUnderRevGuard(msg: HydraOpOddsMsg): void {
    const key = this.keyOf(msg);
    const held = this.staging!.revs.get(key);
    if (held != null && msg.rev <= held) {
      this.diag('staleStagingFrame');
      return;
    }
    this.staging!.revs.set(key, msg.rev);
    this.staging!.frames.set(key, msg);
  }

  private liveFrame(msg: HydraOpOddsMsg): void {
    const epoch = msg.sourceEpoch;
    const matchesCommitted = this.committedEpoch != null && epoch === this.committedEpoch;
    const matchesStaging = this.staging != null && epoch === this.staging.epoch;
    if (!matchesCommitted && !matchesStaging) {
      // Rule 2e: reject + count; an epoch unknown to BOTH authorities means
      // our baseline is gone (e.g. hydra restarted mid-session with no
      // burst in flight) — recover via bounded-backoff re-snapshot. Never
      // mutate state from an unauthenticated epoch.
      this.diag('unknownEpoch');
      this.scheduleRerequest();
      return;
    }
    if (matchesStaging) {
      // Applies to STAGING without touching committed state — this is what
      // keeps a post-restart burst causally safe (rule 2e: Begin(B) stages
      // X@rev1, live B/X@rev2 lands here, End commits rev2 — never rev1).
      this.stageUnderRevGuard(msg);
    }
    if (matchesCommitted) {
      this.applyToCommitted(msg);
    }
  }

  private applyToCommitted(msg: HydraOpOddsMsg): void {
    const key = this.keyOf(msg);
    const held = this.committedRevs.get(key);
    if (held != null && msg.rev <= held) {
      // Post-commit stale straggler (rule 2b) — includes stragglers for
      // tombstoned games, whose revs 2d retains exactly for this check.
      this.diag('staleStraggler');
      return;
    }
    this.committedRevs.set(key, msg.rev);
    if (msg.op.markets.length === 0) {
      // Live no-coverage tombstone (rule 1): delete the value entry; the
      // rev registry entry REMAINS (rule 2d) so reappearance continues the
      // same monotonic sequence while old stragglers still reject.
      this.committed.delete(key);
      this.cb.onLiveDelete(key, msg);
    } else {
      this.committed.set(key, msg);
      this.cb.onLiveUpsert(msg);
    }
  }

  end(msg: HydraOpSnapshotEndMsg): void {
    if (this.staging == null || msg.generation < this.staging.generation) {
      if (this.lastBeginGeneration != null && msg.generation <= this.lastBeginGeneration) {
        // Delayed End from a superseded generation — inert; it must never
        // invalidate active staging (rule 2f, Danielle round-5 race).
        this.diag('staleGeneration');
      } else {
        this.diag('generationAnomaly');
      }
      return;
    }
    if (msg.generation > this.staging.generation) {
      this.diag('generationAnomaly');
      return;
    }
    // End for the ACTIVE generation — integrity checks may discard.
    if (msg.sourceEpoch !== this.staging.epoch) {
      this.staging = null;
      this.diag('mixedEpochBurst');
      this.scheduleRerequest();
      return;
    }
    if (msg.gameCount !== this.staging.received) {
      this.staging = null;
      this.diag('countMismatch');
      this.scheduleRerequest();
      return;
    }
    this.commitStaging();
  }

  private commitStaging(): void {
    const staging = this.staging!;
    this.staging = null;

    // Rebase committed authority (rule 2e). Same-epoch commit MERGES revs
    // max-wins so tombstone-retained revs survive (rule 2d); an epoch
    // change replaces the authority wholesale — the new incarnation's
    // revs-from-1 are the fresh truth.
    if (this.committedEpoch === staging.epoch) {
      for (const [key, rev] of staging.revs) {
        const held = this.committedRevs.get(key);
        if (held == null || rev > held) this.committedRevs.set(key, rev);
      }
    } else {
      this.committedRevs = staging.revs;
      this.committedEpoch = staging.epoch;
    }

    // Value state: the burst's non-tombstone frames, wholesale (rule 9 —
    // games absent from the snapshot drop everywhere).
    const next = new Map<string, HydraOpOddsMsg>();
    for (const [key, frame] of staging.frames) {
      if (frame.op.markets.length > 0) next.set(key, frame);
    }
    this.committed = next;
    this.everCommitted = true;
    this.cancelRerequest();
    this.cancelBurstTimeout();
    this.rerequestAttempts = 0;
    this.cb.onCommit(this.getCommittedFrames());
  }

  private scheduleRerequest(): void {
    if (this.rerequestTimer != null) return;
    const delay =
      REREQUEST_DELAYS_MS[Math.min(this.rerequestAttempts, REREQUEST_DELAYS_MS.length - 1)];
    this.rerequestAttempts++;
    this.rerequestTimer = this.scheduleFn(() => {
      this.rerequestTimer = null;
      this.cb.onRequestSnapshot();
    }, delay);
  }

  private cancelRerequest(): void {
    if (this.rerequestTimer != null) {
      this.cancelFn(this.rerequestTimer);
      this.rerequestTimer = null;
    }
  }

  private cancelBurstTimeout(): void {
    if (this.burstTimeoutTimer != null) {
      this.cancelFn(this.burstTimeoutTimer);
      this.burstTimeoutTimer = null;
    }
  }
}
